import { useRef, useState } from "react";
import { ChevronDown, Trash2 } from "lucide-react";
import { formatMacroValue } from "../../lib/format";
import { ConfidenceChip } from "../progress/ConfidenceChip";
import { SourceChip } from "../SourceChip";
import type { ReviewItem, ReviewItemPatch } from "./review-utils";
import {
  REVIEW_UNIT_OPTIONS,
  confidenceChipLevel,
  itemCompleteness,
  parseOptionalMetric,
} from "./review-utils";

// One editable review row (plan todo 12): name, quantity + unit editors,
// kcal + macros displayed with "—" for undefined (NEVER 0), SourceChip,
// ConfidenceChip, an edit-expand with inline numeric overrides ("Override
// nutrition"), and a remove action (undo handled by the parent snackbar).

export interface ReviewItemCardProps {
  readonly item: ReviewItem;
  readonly index: number;
  // "accurate" logging style extras.
  readonly showPortionWarning: boolean;
  readonly showCompletenessBadge: boolean;
  // "fastest" logging style: macros collapse behind a "Details" disclosure.
  readonly collapseDetails: boolean;
  readonly onPatch: (patch: ReviewItemPatch) => void;
  readonly onRemove: () => void;
}

function MetricCell({ label, value, unit }: {
  readonly label: string;
  readonly value: number | undefined;
  readonly unit: string;
}) {
  return (
    <div className="flex flex-col items-center rounded-lg bg-muted px-1 py-1.5">
      <span className="text-[10px] font-medium uppercase tracking-wide text-text-muted">{label}</span>
      <span className="text-sm font-semibold text-text">
        {formatMacroValue(value, unit)}
      </span>
    </div>
  );
}

interface MetricFieldProps {
  readonly label: string;
  readonly value: string;
  readonly testId: string;
  readonly onChange: (value: string) => void;
}

function MetricField({ label, value, testId, onChange }: MetricFieldProps) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-medium uppercase tracking-wide text-text-muted">{label}</span>
      <input
        type="number"
        inputMode="decimal"
        min="0"
        step="any"
        data-testid={testId}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Unknown"
        className="w-full rounded-lg border border-border bg-surface px-2 py-2 text-base text-text placeholder:text-text-muted focus:border-transparent focus:outline-none focus:ring-2 focus:ring-accent"
      />
    </label>
  );
}

export function ReviewItemCard({
  item,
  index,
  showPortionWarning,
  showCompletenessBadge,
  collapseDetails,
  onPatch,
  onRemove,
}: ReviewItemCardProps) {
  const [expanded, setExpanded] = useState(false);
  const quantityInputRef = useRef<HTMLInputElement>(null);
  const completeness = itemCompleteness(item);
  const chipLevel = confidenceChipLevel(item.confidence);
  const unitListId = `review-units-${index}`;

  const numeric = (value: number | undefined): string => (value === undefined ? "" : String(value));
  const patchMetric = (field: "calories" | "proteinG" | "carbsG" | "fatG" | "fiberG") =>
    (raw: string) => onPatch({ [field]: parseOptionalMetric(raw) });

  return (
    <article
      data-testid="review-item"
      className="rounded-2xl border border-border bg-surface p-3 shadow-sm"
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <input
            type="text"
            data-testid="review-item-name"
            aria-label="Item name"
            value={item.name}
            onChange={(event) => onPatch({ name: event.target.value })}
            placeholder="Ingredient name"
            className="w-full rounded-lg border border-transparent bg-transparent px-1 py-1 text-sm font-semibold text-text placeholder:text-text-muted focus:border-border focus:bg-surface focus:outline-none focus:ring-2 focus:ring-accent"
          />
          <div className="mt-1 flex items-center gap-1.5 px-1">
            <input
              ref={quantityInputRef}
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              data-testid="review-item-quantity"
              aria-label="Quantity"
              value={numeric(item.quantity)}
              onChange={(event) => onPatch({ quantity: parseOptionalMetric(event.target.value) })}
              placeholder="Qty"
              className="w-16 rounded-lg border border-border bg-surface px-2 py-1.5 text-sm text-text placeholder:text-text-muted focus:border-transparent focus:outline-none focus:ring-2 focus:ring-accent"
            />
            <input
              type="text"
              list={unitListId}
              data-testid="review-item-unit"
              aria-label="Unit"
              value={item.unit ?? ""}
              onChange={(event) =>
                onPatch({ unit: event.target.value.trim().length > 0 ? event.target.value : undefined })
              }
              placeholder="unit"
              className="w-24 rounded-lg border border-border bg-surface px-2 py-1.5 text-sm text-text placeholder:text-text-muted focus:border-transparent focus:outline-none focus:ring-2 focus:ring-accent"
            />
            {showPortionWarning && !completeness.hasQuantity && (
              <button
                type="button"
                data-testid="review-add-quantity"
                onClick={() => quantityInputRef.current?.focus()}
                className="inline-flex min-h-[44px] items-center rounded-lg px-2 text-xs font-semibold text-warning transition-all duration-200 ease-out active:opacity-80 active:scale-[0.98] select-none"
              >
                + Add quantity
              </button>
            )}
            <datalist id={unitListId}>
              {REVIEW_UNIT_OPTIONS.map((unit) => (
                <option key={unit} value={unit} />
              ))}
            </datalist>
          </div>
        </div>
        <button
          type="button"
          data-testid="review-item-remove"
          aria-label={`Remove ${item.name}`}
          onClick={onRemove}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-danger transition-all duration-200 ease-out active:bg-danger/10 active:scale-[0.98] select-none"
        >
          <Trash2 size={17} strokeWidth={2} aria-hidden="true" />
        </button>
      </div>

      {collapseDetails ? (
        <div className="mt-2" data-testid="review-item-metrics">
          <div className="grid grid-cols-1 gap-1.5">
            <MetricCell label="kcal" value={item.calories} unit="" />
          </div>
          <details data-testid="review-item-details" className="mt-1.5 rounded-lg bg-muted px-2 py-1.5">
            <summary className="cursor-pointer select-none text-xs font-semibold text-text-muted">
              Details
            </summary>
            <div className="mt-1.5 grid grid-cols-4 gap-1.5">
              <MetricCell label="Prot" value={item.proteinG} unit="g" />
              <MetricCell label="Carb" value={item.carbsG} unit="g" />
              <MetricCell label="Fat" value={item.fatG} unit="g" />
              <MetricCell label="Fibre" value={item.fiberG} unit="g" />
            </div>
          </details>
        </div>
      ) : (
        <div className="mt-2 grid grid-cols-5 gap-1.5" data-testid="review-item-metrics">
          <MetricCell label="kcal" value={item.calories} unit="" />
          <MetricCell label="Prot" value={item.proteinG} unit="g" />
          <MetricCell label="Carb" value={item.carbsG} unit="g" />
          <MetricCell label="Fat" value={item.fatG} unit="g" />
          <MetricCell label="Fibre" value={item.fiberG} unit="g" />
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <SourceChip dataSource={item.dataSource} testId="review-item-source" />
        {chipLevel !== null && <ConfidenceChip level={chipLevel} testId="review-item-confidence" />}
        {showCompletenessBadge && (
          <span
            data-testid="review-completeness-badge"
            className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${
              completeness.complete ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
            }`}
          >
            {completeness.complete ? "Complete" : `${completeness.knownNutrients}/5 nutrition`}
          </span>
        )}
        <button
          type="button"
          data-testid="review-item-edit"
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
          className="ml-auto inline-flex min-h-[44px] items-center gap-1 rounded-lg px-2 text-xs font-semibold text-accent transition-all duration-200 ease-out active:bg-accent-light active:scale-[0.98] select-none"
        >
          {expanded ? "Done" : "Edit nutrition"}
          <ChevronDown
            size={14}
            strokeWidth={2}
            aria-hidden="true"
            className={expanded ? "rotate-180 transition-transform" : "transition-transform"}
          />
        </button>
      </div>

      {showPortionWarning && (!completeness.hasPortion || !completeness.hasCalories) && (
        <p data-testid="review-portion-warning" className="mt-1 text-xs font-medium text-warning">
          {!completeness.hasPortion
            ? "Add a quantity and unit for a more accurate log."
            : "Calories are unknown — add an estimate or leave as unknown (never counted as 0)."}
        </p>
      )}

      {expanded && (
        <div data-testid="review-item-editor" className="mt-2 rounded-xl bg-muted p-3">
          <p className="mb-2 text-xs text-text-muted">
            Override any value below. Leave a field blank when it is unknown — blanks stay unknown
            and are never counted as zero.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <MetricField label="kcal" testId="review-edit-kcal" value={numeric(item.calories)} onChange={patchMetric("calories")} />
            <MetricField label="Protein (g)" testId="review-edit-protein" value={numeric(item.proteinG)} onChange={patchMetric("proteinG")} />
            <MetricField label="Carbs (g)" testId="review-edit-carbs" value={numeric(item.carbsG)} onChange={patchMetric("carbsG")} />
            <MetricField label="Fat (g)" testId="review-edit-fat" value={numeric(item.fatG)} onChange={patchMetric("fatG")} />
            <MetricField label="Fibre (g)" testId="review-edit-fibre" value={numeric(item.fiberG)} onChange={patchMetric("fiberG")} />
          </div>
        </div>
      )}
    </article>
  );
}
